import { Controller } from '@hotwired/stimulus';

/**
 * Export Scale Period Controller
 *
 * « Échelle » and « Période » options of the planning PDF export (plan B, SS16,
 * RB-5, S49). Mounted on a container INSIDE `exports/_execution_plan_pdf_options`,
 * never on the modal envelope: the same partial is hosted by the planning export
 * modal and by the CR annexe form (`_planning_form.html.erb`, inside a
 * `<details>` « Options d'export »).
 *
 * The `<form>` and its « Exporter » button live OUTSIDE the partial, so
 * `connect()` listens to `submit` on `this.element.closest('form')` (precedent
 * `button_loading_controller.js`); it is removed in `disconnect()`.
 *
 * Du / Au (D-10, 2026-09-28) are two `Form::DateInputComponent calendar: true`
 * controls, each driven by its own `date-picker` controller
 * (`date_picker_controller.js`, whose contract this controller only USES):
 * a VISIBLE nameless dd/mm/yyyy text input (`startDisplay` / `endDisplay`) and a
 * HIDDEN ISO input that carries the posted name and value (`start` / `end`).
 * The period is read from the hidden inputs only.
 *
 * Responsibilities:
 * - `periodChanged`: « Du… au… » reveals AND enables the four date inputs;
 *   « Tout le planning » hides AND disables them, so they are never posted
 *   (calque of `moe_fee_assiette_controller.js#applyKind` +
 *   `regularization_mode_controller.js`).
 * - `pageSizeChanged`: swaps the Semaine / Jour tile helps to the checked format
 *   (the formats OFFERED by the partial: five on Planning, A4 · A3 on the CR annex)
 *   from the server-computed `helps` value (the JS never authors a string).
 *   `connect()` runs the same sync (PR D, PM-3): a browser restores the checked
 *   radio on reload, while the server rendered the helps of A3.
 * - `dependenciesChanged` (QA-F14b, decisions 9 and 14): « Délais sur les flèches » is
 *   `disabled` while « Afficher les dépendances » is unchecked (no arrow to carry a delay),
 *   enabled otherwise; greying KEEPS its check. The server renders the same greying from the
 *   RENDERED check; `connect()` re-applies it FIRST, before any step that may throw (revue B7,
 *   CR-2) (RV-19: the partial is re-rendered by
 *   `_export_modal_refresh`, and a browser restores checks on reload), so does the end of
 *   `#resetToRendered` (P9). RV-1: Rails copies `disabled` onto a `check_box`'s hidden « 0 »,
 *   so the partial renders the box `include_hidden: false` after an ALWAYS-enabled hidden
 *   `show_delays=0`. Only the BOX is toggled, never that hidden: Dépendances checked + Délais
 *   unchecked posts `show_delays=0`. Both targets are optional (no-op without either).
 * - `turbo:before-cache` on the document (PR D, D-N-10, SS-D8): every option of the body
 *   (radios and checkboxes) goes back to its RENDERED state before Turbo snapshots the page, so
 *   the Back button reopens on A3 like a new visit; removed in `disconnect()`.
 * - A TYPED date the date-picker refuses (incomplete or unreal dd/mm/yyyy) never
 *   reaches the hidden input: on `blur` the date-picker puts the last valid date
 *   back on screen (`date_picker_controller.js#onBlur`), so the export would
 *   silently use a date the user erased. This controller reads the typed text in
 *   the CAPTURE phase of `blur` on `this.element` (ancestor capture runs before
 *   the date-picker's own `blur` listener on the input), then on the following
 *   `focusout` (which bubbles, after `blur`) compares: typed text non-empty and
 *   no longer displayed ⇒ refused. It then clears the hidden input and dispatches
 *   `change` on it — the date-picker's documented EXTERNAL-write contract
 *   (`#onHiddenChange`) re-seeds the display to empty. The field shows empty, and
 *   the submit guard below reports « Dates incomplètes ». A blur caused by a press
 *   inside the calendar popup is skipped by the date-picker (display untouched),
 *   hence never judged refused. Both listeners are removed in `disconnect()`.
 * - On submit with « Du… au… » checked: a date input still focused (Enter in the
 *   field submits implicitly, without any `blur`) is blurred first, so its text
 *   goes through the date-picker and the rule above. Then an incomplete (empty
 *   hidden), inverted (Au < Du) or unreadable (SS24: a year outside the
 *   server's `PERIOD_YEARS`, or Au later than Du + `PERIOD_MAX_YEARS`, the
 *   mirror of `Exports::PlanningExportOptions#acceptable_period?`) period:
 *   blocks the submit (`preventDefault`, which Turbo honours), re-checks « Tout
 *   le planning » and applies the hide + disable itself (a programmatic check
 *   emits no `change`), shows the inline alert with the server-provided text,
 *   reopens the enclosing `<details>` and moves focus to the « Tout le
 *   planning » radio with `preventScroll` (it is `sr-only`, positioned away
 *   from its tile), then scrolls its visible TILE into view (`block:
 *   'nearest'`), so the focused tile and the alert show together. Checked in
 *   that order, first match wins.
 * - The hidden and text inputs carry no native constraint (no `required`, no
 *   `type="date"`), so the browser's own validation never intercepts the submit.
 *
 * Targets:
 * - pageSize: the `page_size` radios.
 * - scale:    the `scale` radios (their tile help is `[data-radio-segmented-description]`).
 * - period:   the `period` radios (`all` | `range`).
 * - dates:    the wrapper of the two date controls (`hidden` attribute).
 * - start / end: the HIDDEN ISO inputs `period_start` / `period_end` (posted).
 * - startDisplay / endDisplay: the VISIBLE dd/mm/yyyy inputs (nameless).
 * - alert:    the inline `Ui::AlertComponent` (`role="alert"`, `hidden`).
 * - message:  the text node of that alert.
 * - dependenciesBox: the « Afficher les dépendances » checkbox (`show_dependencies`), optional.
 * - delaysBox:       the « Délais sur les flèches » checkbox (`show_delays`, value `1`), optional;
 *                    its hidden `show_delays=0` sibling is deliberately NOT a target.
 *
 * Values:
 * - helps:             `{ <format>: { week, day }, … }` for the OFFERED formats (server,
 *                      `Plans::Pdf::Layout.weeks_per_band`).
 * - invertedMessage:   text shown when Au < Du.
 * - incompleteMessage: text shown when a date is empty or its typed text was refused.
 * - tooLongMessage:    text shown for an SS24 period (`planning.export.period_too_long`).
 * - periodBounds:      `{ minYear, maxYear, maxYears }`, rendered from
 *                      `Exports::PlanningExportOptions::PERIOD_YEARS` / `PERIOD_MAX_YEARS`
 *                      so the client never drifts from the server; absent ⇒ no SS24 check.
 *
 * Actions:
 * - change->export-scale-period#pageSizeChanged (on the `page_size` radios)
 * - change->export-scale-period#periodChanged   (on the `period` radios)
 * - change->export-scale-period#dependenciesChanged (on the « Afficher les dépendances » box)
 *
 * @example
 *   <div data-controller="export-scale-period"
 *        data-export-scale-period-helps-value='{"a4":{"week":"…","day":"…"},"a3":{…},…}'
 *        data-export-scale-period-inverted-message-value="…"
 *        data-export-scale-period-incomplete-message-value="…"
 *        data-export-scale-period-too-long-message-value="…"
 *        data-export-scale-period-period-bounds-value='{"minYear":1900,"maxYear":2200,"maxYears":5}'>
 *     <input type="radio" name="page_size" value="a3"
 *            data-export-scale-period-target="pageSize"
 *            data-action="change->export-scale-period#pageSizeChanged">
 *     <input type="radio" name="period" value="range"
 *            data-export-scale-period-target="period"
 *            data-action="change->export-scale-period#periodChanged">
 *     <div data-export-scale-period-target="dates" hidden>
 *       <div data-controller="date-picker">
 *         <input type="text" data-date-picker-target="display"
 *                data-export-scale-period-target="startDisplay" disabled>
 *         <input type="hidden" name="period_start" value="2026-03-02"
 *                data-date-picker-target="hidden"
 *                data-export-scale-period-target="start" disabled>
 *       </div>
 *       <div data-controller="date-picker">… endDisplay / end (period_end) …</div>
 *     </div>
 *     <input type="checkbox" name="show_dependencies" value="1"
 *            data-export-scale-period-target="dependenciesBox"
 *            data-action="change->export-scale-period#dependenciesChanged">
 *     <input type="hidden" name="show_delays" value="0">
 *     <input type="checkbox" name="show_delays" value="1" checked disabled
 *            data-export-scale-period-target="delaysBox">
 *     <div role="alert" data-export-scale-period-target="alert" hidden>
 *       <p data-export-scale-period-target="message"></p>
 *     </div>
 *   </div>
 */
const DESCRIPTION_SELECTOR = '[data-radio-segmented-description]';
const PERIOD_ALL = 'all';
const PERIOD_RANGE = 'range';

/** Chronological order of two `{ year, month, day }`: negative, 0 or positive. */
function compareDates(a, b) {
  return a.year - b.year || a.month - b.month || a.day - b.day;
}

/**
 * Ruby's `Date#advance(years:)` for comparison purposes: same month and day,
 * `years` later. Ruby clamps 29/02/2024 + 5 years to 28/02/2029; the unclamped
 * `{ 2029, 2, 29 }` sorts between 28/02 and 01/03 under `compareDates`, so every
 * REAL end date lands on the same side of both limits.
 */
function advanceYears(date, years) {
  return { ...date, year: date.year + years };
}

export default class extends Controller {
  static targets = [
    'pageSize',
    'scale',
    'period',
    'dates',
    'start',
    'end',
    'startDisplay',
    'endDisplay',
    'alert',
    'message',
    'dependenciesBox',
    'delaysBox',
  ];

  static values = {
    helps: Object,
    invertedMessage: String,
    incompleteMessage: String,
    tooLongMessage: String,
    periodBounds: Object,
  };

  connect() {
    // RV-19: the partial is re-rendered by `_export_modal_refresh` and the browser may restore
    // « Dépendances » on reload; the server greyed « Délais » from the RENDERED check. FIRST
    // (revue B7, CR-2): a later step that throws must not leave « Délais » disabled while
    // « Dépendances » shows checked (only the hidden `show_delays=0` would be posted).
    this.#syncDelaysBox();
    this.form = this.element.closest('form');
    this.typed = null;
    this.boundSubmit = this.#handleSubmit.bind(this);
    this.boundBlur = this.#rememberTypedDate.bind(this);
    this.boundFocusOut = this.#clearRefusedDate.bind(this);
    this.form?.addEventListener('submit', this.boundSubmit);
    // `blur` does not bubble: seen from the partial only in the capture phase,
    // which runs BEFORE the date-picker's own `blur` listener on the input.
    this.element.addEventListener('blur', this.boundBlur, true);
    // `focusout` bubbles and fires AFTER `blur`: the date-picker has ruled.
    this.element.addEventListener('focusout', this.boundFocusOut);
    // PM-3: the browser may restore a checked format on reload; the server rendered the helps of A3.
    this.#syncHelps();
    // D-N-10: Turbo snapshots the page for the Back button at `turbo:before-cache`.
    this.document = this.element.ownerDocument;
    this.boundBeforeCache = this.#resetToRendered.bind(this);
    this.document?.addEventListener('turbo:before-cache', this.boundBeforeCache);
  }

  disconnect() {
    this.form?.removeEventListener('submit', this.boundSubmit);
    this.element.removeEventListener('blur', this.boundBlur, true);
    this.element.removeEventListener('focusout', this.boundFocusOut);
    this.document?.removeEventListener('turbo:before-cache', this.boundBeforeCache);
    this.form = null;
    this.document = null;
    this.typed = null;
  }

  periodChanged(_event) {
    const rangeActive = this.#checkedValue(this.periodTargets) === PERIOD_RANGE;
    this.#applyPeriod(rangeActive);
    // The alert describes a fallback to « Tout le planning »; once the user
    // picks « Du… au… » again it no longer holds.
    if (rangeActive) this.#hideAlert();
  }

  pageSizeChanged(_event) {
    this.#syncHelps();
  }

  dependenciesChanged(_event) {
    this.#syncDelaysBox();
  }

  // QA-F14b (RV-1): only the CHECKBOX is toggled, never the always-enabled hidden `show_delays=0`
  // placed before it, so « Délais » unchecked with « Dépendances » checked still posts `0`. Greying
  // keeps the check: re-enabling restores the user's choice.
  #syncDelaysBox() {
    if (!this.hasDependenciesBoxTarget || !this.hasDelaysBoxTarget) return;

    this.delaysBoxTarget.disabled = !this.dependenciesBoxTarget.checked;
  }

  #syncHelps() {
    const helps = this.helpsValue?.[this.#checkedValue(this.pageSizeTargets)];
    if (!helps) return;

    this.scaleTargets.forEach((radio) => {
      const text = helps[radio.value];
      if (typeof text !== 'string') return;

      const description = this.#tileDescription(radio);
      if (description) description.textContent = text;
    });
  }

  // D-N-10 (SS-D8): coming back with the Back button is a NEW opening for the user,
  // so the cached snapshot must hold the RENDERED choices (the `checked` attributes:
  // A3 and the other defaults on Planning, the replayed options on the CR annex),
  // not the ones picked before leaving. Staying on the page keeps the choice. Every
  // radio and checkbox of the body goes back, the display cards and the actor
  // picker included (`export-actor-filter` recounts on its reconnect).
  #resetToRendered() {
    this.element
      .querySelectorAll('input[type="radio"], input[type="checkbox"]')
      .forEach((input) => {
        input.checked = input.defaultChecked;
      });
    this.#applyPeriod(this.#checkedValue(this.periodTargets) === PERIOD_RANGE);
    this.#hideAlert();
    this.#syncHelps();
    // P9: the checks went back to their rendered state; the greying must follow.
    this.#syncDelaysBox();
  }

  #handleSubmit(event) {
    if (this.#checkedValue(this.periodTargets) !== PERIOD_RANGE) return;

    this.#blurFocusedDate();
    const message = this.#periodError();
    if (!message) return;

    event.preventDefault();
    this.#fallBackToAll(message);
  }

  /**
   * Enter in a date field submits the form implicitly, WITHOUT a `blur`: the
   * typed text has not been through the date-picker yet. Blurring it runs the
   * date-picker's own commit / revert, then `#clearRefusedDate`, synchronously.
   */
  #blurFocusedDate() {
    const active = this.element.ownerDocument?.activeElement;
    if (active && this.#datePair(active)) active.blur();
  }

  /** Capture-phase `blur`: the text as typed, before the date-picker rules on it. */
  #rememberTypedDate(event) {
    const pair = this.#datePair(event.target);
    this.typed = pair ? { display: pair.display, text: pair.display.value.trim() } : null;
  }

  /**
   * `focusout`, after the date-picker's `blur`: a non-empty typed text that is no
   * longer displayed was REFUSED (the date-picker put the last valid date back).
   * Clear the posted value through the date-picker's external-write contract, so
   * the field shows empty and the submit guard reports « Dates incomplètes ».
   */
  #clearRefusedDate(event) {
    const pair = this.#datePair(event.target);
    const typed = this.typed;
    this.typed = null;
    if (!pair || typed?.display !== pair.display) return;
    if (typed.text === '' || pair.display.value === typed.text) return;

    pair.hidden.value = '';
    pair.hidden.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** @returns {{display: HTMLInputElement, hidden: HTMLInputElement}|null} */
  #datePair(target) {
    if (this.hasStartDisplayTarget && this.hasStartTarget && target === this.startDisplayTarget) {
      return { display: this.startDisplayTarget, hidden: this.startTarget };
    }
    if (this.hasEndDisplayTarget && this.hasEndTarget && target === this.endDisplayTarget) {
      return { display: this.endDisplayTarget, hidden: this.endTarget };
    }
    return null;
  }

  /** @returns {string|null} the alert text when the period cannot be exported. */
  #periodError() {
    if (!this.hasStartTarget || !this.hasEndTarget) return null;

    const start = this.#isoDate(this.startTarget);
    const end = this.#isoDate(this.endTarget);
    if (!start || !end) return this.incompleteMessageValue;
    // A one-day period (Au = Du) is valid.
    if (compareDates(end, start) < 0) return this.invertedMessageValue;
    if (this.#tooLong(start, end)) return this.tooLongMessageValue;

    return null;
  }

  /**
   * SS24, mirror of `Exports::PlanningExportOptions#acceptable_period?`: both
   * years within `PERIOD_YEARS` and `last.between?(first, first.advance(years:
   * PERIOD_MAX_YEARS))`. The bounds come from the server (`periodBounds` value);
   * without them the check is skipped (the server still omits the period).
   */
  #tooLong(start, end) {
    const { minYear, maxYear, maxYears } = this.periodBoundsValue ?? {};
    if (![minYear, maxYear, maxYears].every(Number.isInteger)) return false;

    const outOfYears = [start, end].some((date) => date.year < minYear || date.year > maxYear);
    return outOfYears || compareDates(end, advanceYears(start, maxYears)) > 0;
  }

  /** @returns {{year: number, month: number, day: number}|null} null when empty or unreadable. */
  #isoDate(input) {
    // The hidden ISO value, written by the server (`Date#iso8601`) or by the
    // date-picker. A year may have MORE than 4 digits (`10000-01-01`), so the
    // parts are compared as numbers, never as strings.
    const match = /^(\d{4,})-(\d{2})-(\d{2})$/.exec(input.value ?? '');
    if (!match) return null;

    const [year, month, day] = match.slice(1).map(Number);
    return { year, month, day };
  }

  #fallBackToAll(message) {
    const allRadio = this.periodTargets.find((radio) => radio.value === PERIOD_ALL);
    this.periodTargets.forEach((radio) => {
      radio.checked = radio === allRadio;
    });
    this.#applyPeriod(false);
    this.#showAlert(message);

    const details = this.element.closest('details');
    if (details && !details.open) details.open = true;

    // The radio is `sr-only` (absolutely positioned away from its tile): a bare
    // `focus()` scrolls the modal body to the radio and leaves the focused tile
    // out of view. Focus without scrolling, then bring the visible tile in.
    allRadio?.focus({ preventScroll: true });
    this.#tileOf(allRadio)?.scrollIntoView?.({ block: 'nearest' });
  }

  #applyPeriod(rangeActive) {
    if (this.hasDatesTarget) this.datesTarget.hidden = !rangeActive;
    this.#dateInputs().forEach((input) => {
      input.disabled = !rangeActive;
    });
  }

  /** The posted hidden ISO inputs and their visible dd/mm/yyyy inputs. */
  #dateInputs() {
    return [
      this.hasStartTarget && this.startTarget,
      this.hasEndTarget && this.endTarget,
      this.hasStartDisplayTarget && this.startDisplayTarget,
      this.hasEndDisplayTarget && this.endDisplayTarget,
    ].filter(Boolean);
  }

  #showAlert(message) {
    // Text first, then reveal, so `role="alert"` announces the new message.
    if (this.hasMessageTarget) this.messageTarget.textContent = message ?? '';
    if (this.hasAlertTarget) this.alertTarget.hidden = false;
  }

  #hideAlert() {
    if (this.hasAlertTarget) this.alertTarget.hidden = true;
  }

  #checkedValue(radios) {
    return radios.find((radio) => radio.checked)?.value ?? null;
  }

  #tileDescription(radio) {
    return this.#tileOf(radio)?.querySelector(DESCRIPTION_SELECTOR) ?? null;
  }

  /** The visible `<label>` tile of a `Ui::RadioSegmentedComponent` radio. */
  #tileOf(radio) {
    if (!radio) return null;
    return radio.closest?.('label') ?? radio.parentElement ?? null;
  }
}
